<template>
  <div class="agent-table-container relative">
    <!-- Fixed height to match the sibling Latest Calls card. -->
    <div class="agent-table-wrap relative">
      <ve-table
        v-show="agentMetrics.length > 0"
        style="height: 100%;"
        :fixed-header="true"
        :columns="columns"
        :table-data="tableData"
      />
      <div
        v-if="!isLoading && !agentMetrics.length"
        class="agent-table-empty-overlay"
      >
        <empty-state
          :title="$t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.NO_AGENTS')"
        />
      </div>
    </div>
    <div
      v-if="isLoading"
      class="agents-loader-overlay"
    >
      <div class="agents-loader-content">
        <spinner size="48" />
        <span class="ml-4 font-bold text-base">{{
          $t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.LOADING_MESSAGE')
        }}</span>
      </div>
    </div>
    <div v-if="agentMetrics.length > 0" class="table-pagination">
      <ve-pagination
        :total="agents.length"
        :page-index="pageIndex"
        :page-size="25"
        :page-size-option="[25]"
        @on-page-number-change="onPageNumberChange"
      />
    </div>
    <!-- Reserve pagination row height when empty. -->
    <div v-else class="w-full h-[57px]"></div>
  </div>
</template>

<script lang="jsx">
import { h } from 'vue';
import { VeTable, VePagination } from 'vue-easytable';
import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import rtlMixin from 'shared/mixins/rtlMixin';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';

export default {
  name: 'AgentTable',
  components: {
    EmptyState,
    Spinner,
    VeTable,
    VePagination,
  },
  mixins: [rtlMixin],
  props: {
    agents: {
      type: Array,
      default: () => [],
    },
    agentMetrics: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    pageIndex: {
      type: Number,
      default: 1,
    },
  },
  computed: {
    tableData() {
      return this.agentMetrics.map(agent => {
        const agentInformation = this.getAgentInformation(agent.id) || {};
        
        return {
          agent: agentInformation.name || 'Unknown',
          email: agentInformation.email || 'N/A',
          thumbnail: agentInformation.thumbnail || '', 
          open: agent.metric.open || 0,                 
          unattended: agent.metric.unattended || 0,   
          status: agentInformation.availability_status || 'Unknown',
        };
      });
    },
    columns() {
      return [
        {
          field: 'agent',
          key: 'agent',
          title: this.$t(
            'OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.AGENT'
          ),
          fixed: 'left',
          align: this.isRTLView ? 'right' : 'left',
          width: 25,
          renderBodyCell: ({ row }) => {
            return h('div', { class: 'row-user-block flex flex-row flex-nowrap items-center text-left w-full min-w-0' }, [
              h('div', { class: 'flex items-center shrink-0' }, [
                h(Thumbnail, {
                  props: {
                    src: row.thumbnail,
                    size: '32px',
                    username: row.agent,
                    status: row.status
                  }
                }),
              ]),
              h('div', { class: 'flex flex-col items-start min-w-0 flex-1', style: { marginLeft: '12px' } }, [
                h('h6', { class: 'overflow-hidden whitespace-nowrap text-ellipsis text-sm m-0 leading-[1.2] text-slate-800 dark:text-slate-100 max-w-full' }, row.agent),
                h('span', { class: 'block overflow-hidden whitespace-nowrap text-ellipsis text-xs text-slate-600 dark:text-slate-200 max-w-full' }, row.email)
              ])
            ]);
          }        
        },
        {
          field: 'open',
          key: 'open',
          title: this.$t(
            'OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.OPEN'
          ),
          align: 'center',
          width: 7,
        },
        {
          field: 'unattended',
          key: 'unattended',
          title: this.$t(
            'OVERVIEW_REPORTS.AGENT_CONVERSATIONS.TABLE_HEADER.UNATTENDED'
          ),
          align: 'center',
          width: 10,
        },
      ];
    },
  },
  methods: {
    onPageNumberChange(pageIndex) {
      this.$emit('page-change', pageIndex);
    },
    getAgentInformation(id) {
      return this.agents.find(agent => agent.id === Number(id));
    },
  },
};
</script>

<style lang="scss" scoped>
.agent-table-container {
  @apply flex flex-col flex-1;
  position: relative; // Ensure overlay is positioned relative to this container

  // Fixed-height area; matches .call-history-table-wrap.
  .agent-table-wrap {
    height: 400px;
  }

  // Keep scroll behavior, hide scrollbar tracks (matches Priority Leads).
  .agent-table-wrap :deep(.ve-table-container) {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .agent-table-wrap :deep(.ve-table-container::-webkit-scrollbar) {
    width: 0;
    height: 0;
    display: none;
  }

  .agent-table-empty-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ve-table {
    &:deep {
      th.ve-table-header-th {
        font-size: var(--font-size-mini) !important;
        padding: var(--space-small) var(--space-two) !important;
      }

      td.ve-table-body-td {
        padding: var(--space-one) var(--space-two) !important;
      }
    }
  }

  &:deep(.ve-pagination) {

    @apply bg-transparent dark:bg-transparent;
  }

  &:deep(.ve-pagination-select) {

    @apply hidden;
  }


  .table-pagination {
    @apply mt-4 text-right;
  }
}

.agents-loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  // background: rgba(255,255,255,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
}

.agents-loader-content {
  display: flex;
  align-items: center;
  justify-content: center;
}

.agents-loader {
  @apply items-center flex text-base justify-center p-8;
}
</style>
